import React from "react";
import { useTranslate } from "@refinedev/core";
import {
  ChartBarIcon,
  GearIcon,
  PencilSimpleIcon,
} from "@phosphor-icons/react";
import { Stack, radius } from "@libertify/ui";
import styled from "styled-components";
import { Space, Switch, Tooltip } from "antd";
import { useAntTheme } from "hooks/useAntTheme";

export type CreatorToolbarProps = {
  publishedPublicationUrl: string;
  isLive: boolean;
  onAnalyticsClick: () => void;
  onEditVideoClick: () => void;
  onEditClick: () => void;
  onShareClick: () => void;
  onPublishToggle: (checked: boolean) => void;
  isUnpublishing: boolean;
  secondaryMenu?: React.ReactNode;
  isHeyGenius?: boolean;
  /** False hides "Edit video" (HeyGenius Doc can't open the video editor). */
  canEditVideo?: boolean;
  /** Compact out-of-date button (regenerate/update), rendered after Share. */
  outOfDateAction?: React.ReactNode;
};

const ToolbarWrapper = styled.div`
  flex-shrink: 0;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: white;
  border-bottom: 1px solid #f3f4f6;
`;

const LogoImg = styled.img`
  height: 24px;
  width: auto;
`;

const GhostButton = styled.button`
  border: 0;
  border-radius: ${radius.pill}px;
  padding: 6px 14px;
  font-size: 13px;
  color: #374151;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;

  &:hover {
    background: #f9fafb;
  }
`;

const ShareButton = styled.button`
  background: #14b8a6;
  color: white;
  border: 3px solid #3bd4ca;
  border-radius: ${radius.pill}px;
  padding: 10px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;

  &:hover {
    background: #0d9488;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusPill = styled.span<{ $published: boolean }>`
  background: ${(p) => (p.$published ? "#ecfdf5" : "#f9fafb")};
  color: ${(p) => (p.$published ? "#059669" : "#9ca3af")};
  border-radius: ${radius.pill}px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
  display: inline-block;
`;

const NavDivider = styled.div`
  width: 1px;
  height: 40px;
  background: #e5e7eb;
`;

export const CreatorToolbar: React.FC<CreatorToolbarProps> = ({
  isLive,
  onAnalyticsClick,
  onEditVideoClick,
  onEditClick,
  onShareClick,
  onPublishToggle,
  isUnpublishing,
  secondaryMenu,
  isHeyGenius,
  canEditVideo = true,
  outOfDateAction,
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const showFullActions = !isHeyGenius;

  return (
    <ToolbarWrapper>
      <LogoImg
        src="/images/Logo_Libertify_Horizontal_fond_blanc_RVB.png"
        alt="Libertify"
      />
      <Stack direction="horizontal" align="center" gap="sm">
        {showFullActions && (
          <>
            <GhostButton onClick={onAnalyticsClick}>
              <ChartBarIcon size={14} />
              {t("media.components.CreatorToolbar.analytics")}
            </GhostButton>
            {canEditVideo ? (
              <GhostButton onClick={onEditVideoClick}>
                <PencilSimpleIcon size={14} />
                {t("media.components.CreatorToolbar.editVideo")}
              </GhostButton>
            ) : null}
          </>
        )}
        <GhostButton onClick={onEditClick}>
          <GearIcon size={14} />
          {t("media.components.CreatorToolbar.settings")}
        </GhostButton>
        {showFullActions && (
          <>
            <ShareButton onClick={onShareClick}>
              {t("media.components.CreatorToolbar.share")}
            </ShareButton>
            {outOfDateAction}
            <NavDivider />
            <Space size="small">
              <Tooltip
                title={t("media.components.ProjectSettingsDrawer.videoStatus")}
                mouseEnterDelay={0.3}
              >
                <StatusPill $published={isLive}>
                  ●{" "}
                  {isLive
                    ? t(
                        "media.components.ProjectSettingsDrawer.published",
                        "Published"
                      )
                    : t("media.components.ProjectSettingsDrawer.draft")}
                </StatusPill>
              </Tooltip>
              <Switch
                checked={isLive}
                onChange={onPublishToggle}
                loading={isUnpublishing}
                style={{
                  backgroundColor: isLive ? "#14B8A6" : theme.colorTextDisabled,
                }}
              />
            </Space>
          </>
        )}
        {secondaryMenu}
      </Stack>
    </ToolbarWrapper>
  );
};
